import { Layout } from "@/components/Layout";

const MAPPING = [
    ["Party / agent", "Seller Custodian, Buyer Custodian, Transfer Agent"],
    ["Financial instrument", "Instrument reference and tokenised asset profile"],
    ["Account / custody reference", "Synthetic custody reference"],
    ["Securities transaction", "Event ID, action, quantity, date"],
    ["Status / reason", "Allow, freeze, review, reason code"],
    ["Settlement / execution reference", "Synthetic execution reference"],
    ["Supplementary digital-asset control data", "Policy ID, receipt hash, control-decision reference"],
];

const CONTEXT = [
    {
        title: "EU DLT Pilot Regime",
        body: "Relevant context for experimentation with DLT market infrastructures and tokenised financial instruments. Not a claim of authorisation under the regime.",
    },
    {
        title: "DORA",
        body: "Relevant context for operational resilience, traceability, control and exception handling in financial-entity workflows. Not a compliance attestation.",
    },
    {
        title: "Swift ISO 20022 - November 2026 readiness",
        body: "Relevant context for structured data quality and repair processes in payment-related workflows. From 14 November 2026, unstructured postal addresses are not accepted in the relevant payments context, with risk of rejection or delay. Used here only as a realistic structured-data/repair trigger - not a tokenised-securities rule.",
    },
];

export default function Standards() {
    return (
        <Layout>
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">
                Structured business data
            </p>
            <h1 className="mt-3 text-3xl font-medium tracking-tight text-slate-900">
                ISO 20022-aligned mapping
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
                Illustrative mapping concepts only. This sandbox demonstrates the
                operating pattern, not a certified message implementation.
            </p>

            <div className="mt-8 overflow-hidden rounded-sm border border-slate-200 bg-white">
                <table className="w-full border-collapse text-sm" data-testid="iso-mapping-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="px-5 py-3 text-left font-mono text-xs uppercase tracking-wide text-slate-500">
                                Business concept
                            </th>
                            <th className="px-5 py-3 text-left font-mono text-xs uppercase tracking-wide text-slate-500">
                                Sandbox field
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {MAPPING.map(([concept, field]) => (
                            <tr key={concept} className="border-b border-slate-100 last:border-0">
                                <td className="px-5 py-3 text-slate-800">{concept}</td>
                                <td className="px-5 py-3 text-slate-600">{field}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="mt-6 max-w-3xl rounded-sm border border-slate-200 bg-slate-50 p-4 text-xs leading-relaxed text-slate-500">
                The final field-level mapping is intended for validation against
                Swift-provided ISO 20022 business components and templates. This sandbox
                demonstrates the operating pattern, not a certified message
                implementation.
            </p>

            {/* Supplementary governance context */}
            <section className="mt-14" data-testid="supplementary-governance">
                <h2 className="text-2xl font-medium tracking-tight text-slate-900">
                    Governance context carried alongside structured business data
                </h2>
                <div className="mt-6 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                        "Institutional role reference",
                        "Delegated authority scope",
                        "Policy ID and version",
                        "Protocol interaction reference",
                        "Evidence-reference state",
                        "Control decision",
                        "KATLAS receipt reference",
                        "Downstream execution-adapter reference",
                    ].map((g) => (
                        <div key={g} className="bg-white p-4">
                            <p className="text-sm text-slate-700">{g}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-4 max-w-3xl text-xs leading-relaxed text-slate-500">
                    This illustrates supplementary governance context for a
                    standards-oriented control model. Final field-level message mapping
                    remains subject to the Swift-provided business components and
                    templates.
                </p>
            </section>

            {/* Segregated governance contexts */}
            <section className="mt-14 rounded-sm border border-slate-200 bg-white p-8" data-testid="segregated-governance">
                <h2 className="text-2xl font-medium tracking-tight text-slate-900">
                    Segregated governance contexts
                </h2>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
                    KATLAS supports separate permissioned governance channels where
                    distinct groups require their own common-interest state, policy
                    boundaries and receipt history. This allows a market workflow to
                    coordinate the authorised outcome without turning the shared layer
                    into a repository for raw institutional evidence.
                </p>
            </section>

            <section className="mt-14" data-testid="what-katlas-replaces">
                <h2 className="text-2xl font-medium tracking-tight text-slate-900">
                    What KATLAS replaces
                </h2>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
                    Language for market infrastructures and regulated institutions -
                    runtime operational governance, not generic workflow automation.
                </p>
                <div className="mt-6 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 md:grid-cols-2">
                    <div className="bg-white p-6">
                        <p className="font-mono text-xs uppercase tracking-wide text-slate-400">
                            Without a governance rail
                        </p>
                        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-600">
                            <li>· each token or platform embeds its own control logic;</li>
                            <li>· evidence is copied between systems;</li>
                            <li>· responsibility is hard to prove across institutions;</li>
                            <li>· exceptions become manual repair activity;</li>
                            <li>· the execution record may show what happened but not who was authorised to cause it.</li>
                        </ul>
                    </div>
                    <div className="bg-white p-6">
                        <p className="font-mono text-xs uppercase tracking-wide text-blue-950">
                            With KATLAS CARops
                        </p>
                        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-700">
                            <li>· control is reusable across asset types;</li>
                            <li>· evidence remains with the accountable institution;</li>
                            <li>· authority is expressed before execution;</li>
                            <li>· handoffs are role-bound and policy-bound;</li>
                            <li>· a verified receipt proves the governed outcome.</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section className="mt-14">
                <h2 className="text-2xl font-medium tracking-tight text-slate-900">
                    Design context
                </h2>
                <div className="mt-6 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 md:grid-cols-3">
                    {CONTEXT.map((c) => (
                        <div key={c.title} className="bg-white p-6">
                            <h3 className="text-base font-medium text-slate-900">{c.title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.body}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate-400">
                    No claim of compliance, certification, regulatory approval, legal
                    advice or production-market-infrastructure readiness is made.
                </p>
            </section>
        </Layout>
    );
}
